import {defineStore} from "pinia";
import {computed, reactive, ref, watch} from "vue";
import {isEqual} from "lodash";
import {educationComplete, exceptionalAbilityComplete, workHistoryAnswered} from "../helpers/CandidateFunctions.js";
import {objectToCamelCase} from "../helpers/CaseTransformer.js";
import {useStorage} from "@vueuse/core";
import {useAccountStore} from "./AccountStore.js";
import {useCandidateProfileStore} from "./CandidateProfileStore.js";
import {gtagApplicationSubmitted} from "../helpers/GoogleAnalytics.js";
import {handleSubmitApplicationHelper} from "../helpers/SubmitApplication";
import AccountApi from "../api/AccountApi.js";
import CandidateOpportunityApi from "../api/CandidateOpportunityApi.js";

export const useCandidateApplicationFormStore = defineStore("candidateApplicationForm", () => {
  const applicationForm = reactive({
    id: null,
    uuid: "",
    screeningQuestions: [],
    companyName: "",
    companyLogo: "",
    companyAvatar: "",
    candidate: null,
    hasApplied: false,
    dailyRemainingApplications: 10,
    loading: true,
    submitting: false,
    primaryForeground: "",
    primaryBackground: "",
    secondaryForeground: "",
    secondaryBackground: "",
    estimatedCompletionTime: "",
    companyImage: "",
    image1: "",
    image2: "",
    image3: "",
    image4: "",
    image5: "",
    banner: "",
    highlightVideo: "",
    opportunityTitle: "",
    compensation: "",
    remoteType: "",
    employmentTypes: "",
    location: "",
    hiresNeeded: "",
    verified: true,
  });

  const furthestStep = reactive({
    name: "",
    at: null,
    totalSteps: 4,
    completedSteps: 0,
  });

  const setFurthestStep = (stepNumber, name) => {
    if (stepNumber < furthestStep.completedSteps) return; // Still allow updates if it's the same step
    furthestStep.name = name;
    furthestStep.at = new Date();
    if (stepNumber > furthestStep.completedSteps) furthestStep.completedSteps = stepNumber;
  };

  const screeningQuestionAnswers = reactive([]);

  const defaultNewUserDetails = {
    firstName: "",
    lastName: "",
    email: "",
    phone: 0,
    username: "",
    location: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    lat: 0.0,
    lng: 0.0,
    tosChecked: false,
    enableDefaultSmsNotifications: true,
  };

  const newUserDetails = useStorage("new-user", defaultNewUserDetails, localStorage, {mergeDefaults: true});

  const newUserPassword = reactive({
    smsVerificationCode: "",
    password: "",
    passwordConfirmation: "",
  });

  const candidateProfileRequiredFieldDefaults = () => ({
    education: [],
    careerPassport: [],
    exceptionalAbility: [],
    noPreviousWorkExperience: false,
  });

  const account = useAccountStore();
  const draft = useStorage("candidate-profile-required-v2", {
    owner: null,
    profile: candidateProfileRequiredFieldDefaults(),
    serverProfile: null,
  }, localStorage, {mergeDefaults: true});
  const state = computed({get: () => draft.value.profile, set: (profile) => draft.value.profile = profile});
  const draftOwner = () => account.user?.id ? `user:${account.user.id}` : "guest";
  let registeringApplicant = false;
  let formRequest = 0;
  const clearProfileDraft = () => {
    draft.value = {owner: draftOwner(), profile: candidateProfileRequiredFieldDefaults(), serverProfile: null};
  };

  watch(() => account.user?.id, (id, previousId) => {
    if (registeringApplicant) {
      draft.value.owner = draftOwner();
      return;
    }
    if (!previousId && draft.value.owner === draftOwner()) return;
    clearProfileDraft();
    applicationForm.candidate = null;
    showCandidateCompleteProfile.value = true;
  }, {flush: "sync"});

  watch(() => state.value.careerPassport?.length, (length) => {
    if (length) state.value.noPreviousWorkExperience = false;
  }, {flush: "sync"});

  watch(() => newUserDetails.value.email, (email, previousEmail) => {
    if (!account.user?.id && previousEmail && email !== previousEmail) clearProfileDraft();
  }, {flush: "sync"});

  const showCandidateCompleteProfile = ref(false);
  const usedResumeParser = ref(false);
  const emailCheckedAt = ref(null);
  const phoneCheckedAt = ref(null);
  const lastPhoneCodeSentTo = ref(false);
  const verifiedPhoneNumber = ref(null);

  const setShowCandidateCompleteProfile = () => {
    const {candidate} = applicationForm;
    if (!candidate) return showCandidateCompleteProfile.value = true;

    const {careerPassport, noPreviousWorkExperience, education, exceptionalAbility} = state.value;

    showCandidateCompleteProfile.value = !(
      workHistoryAnswered(careerPassport, noPreviousWorkExperience) &&
      educationComplete(education?.[0]) &&
      exceptionalAbilityComplete(exceptionalAbility)
    );
  };

  const getApplicationForm = async (uuid) => {
    const api = new CandidateOpportunityApi();
    // Public pages bootstrap the account asynchronously; establish identity before owning the draft.
    if (account.user === undefined) await account.fetchUserAccount();
    const owner = draftOwner();
    const request = ++formRequest;
    if (draft.value.owner !== owner) clearProfileDraft();

    applicationForm.loading = true;

    try {
      const response = await api.getApplicationForm(uuid);
      if (request !== formRequest || owner !== draftOwner()) return;
      Object.assign(applicationForm, objectToCamelCase(response.data));
      applicationForm.uuid = uuid;
      if (screeningQuestionAnswers.value?.length !== applicationForm.screeningQuestions?.length) {
        screeningQuestionAnswers.value = applicationForm.screeningQuestions.map((question) => ({
          question: question.question,
          answer: null,
        }));
      }
      if (applicationForm.candidate) {
        const candidate = applicationForm.candidate;
        const serverProfile = {
          education: candidate.education || [],
          careerPassport: candidate.careerPassport || [],
          exceptionalAbility: candidate.exceptionalAbility || [],
          noPreviousWorkExperience: candidate.noPreviousWorkExperience === true && !candidate.careerPassport?.length,
        };
        // Preserve this applicant's edits across refresh only while the saved profile is unchanged.
        if (!isEqual(serverProfile, draft.value.serverProfile)) {
          state.value = JSON.parse(JSON.stringify(serverProfile));
          draft.value.serverProfile = JSON.parse(JSON.stringify(serverProfile));
        }
      }
      if (!newUserDetails.value?.lat || !newUserDetails.value?.lng) {
        Object.assign(newUserDetails.value, {
          city: "",
          state: "",
          postalCode: "",
          country: "",
          lat: 0.0,
          lng: 0.0,
        });
      }
      furthestStep.totalSteps = 4 + Math.ceil(applicationForm.screeningQuestions.length / 10);
      setShowCandidateCompleteProfile();
    } catch (e) {
      console.error(e);
    } finally {
      if (request === formRequest) applicationForm.loading = false;
    }
  };

  const handleCreateNewUser = async () => {
    // Make sure lat & lng are numbers
    let lat = newUserDetails.value?.lat ? parseFloat(newUserDetails.value?.lat) : null;
    let lng = newUserDetails.value?.lng ? parseFloat(newUserDetails.value?.lng) : null;

    const newUserData = {
      first_name: newUserDetails.value?.firstName || "",
      last_name: newUserDetails.value?.lastName || "",
      email: newUserDetails.value?.email || "",
      phone: newUserDetails.value?.phone || null,
      username: newUserDetails.value?.username || "",
      location: newUserDetails.value?.location || "",
      city: newUserDetails.value?.city || "",
      state: newUserDetails.value?.state || "",
      postal_code: newUserDetails.value?.postalCode || "",
      country: newUserDetails.value?.country || "US",
      lat: lat,
      lng: lng,
      tos_checked: newUserDetails.value?.tosChecked || false,
      enable_default_sms_notifications: newUserDetails.value?.enableDefaultSmsNotifications || true,
      sms_verification_code: newUserPassword.smsVerificationCode,
      password: newUserPassword.password,
      password_confirmation: newUserPassword.passwordConfirmation,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || navigator.userLanguage,
      create_company: false,
      acquisition_source: localStorage.getItem("acquisition_source") || "",
    };

    try {
      await AccountApi.registerUser(newUserData);
    } catch (e) {
      console.error("Failed to create new user", e);
      return false;
    }

    try {
      registeringApplicant = true;
      const accountStore = useAccountStore();
      await accountStore.fetchUserAccount();
      return true;
    } catch (e) {
      console.error("Failed to fetch user account", e);
      return false;
    } finally {
      registeringApplicant = false;
    }

  };

  const handleCompleteProfile = async () => {
    const accountStore = useAccountStore();
    const profileStore = useCandidateProfileStore();

    if (!accountStore.user?.candidateId) {
      throw new Error("The user is not signed in");
    }

    Object.assign(profileStore.state, state.value);

    profileStore.state.lat = undefined;
    profileStore.state.lng = undefined;

    let success = await profileStore.update(undefined, false, true);
    if (!success) throw new Error("Failed to update profile");
  };

  const handleSubmitApplication = async () => {

    await handleSubmitApplicationHelper(
      applicationForm.id,
      applicationForm.uuid,
      screeningQuestionAnswers
    );

  };

  const handleSubmit = async () => {
    if (applicationForm.submitting) return;

    applicationForm.submitting = true;
    window.progressBar.start();

    const accountStore = useAccountStore();

    const shouldCreateNewUser = !accountStore.isLoggedIn;
    const shouldCompleteProfile = usedResumeParser.value || showCandidateCompleteProfile.value ||
      !isEqual(state.value, draft.value.serverProfile);

    try {
      if (shouldCreateNewUser && !await handleCreateNewUser()) throw new Error("Failed to create applicant account");
      if (shouldCompleteProfile) await handleCompleteProfile();
      await handleSubmitApplication();
      gtagApplicationSubmitted(applicationForm.uuid);
      document.dispatchEvent(new CustomEvent("bd-created-user", {detail: {firstName: newUserDetails.value?.firstName}}));
      window.progressBar.complete();
      reset();
      applicationForm.submitting = false;
      return true;
    } catch (e) {
      console.error("Failed to submit application", e);
      try {
        await getApplicationForm(applicationForm.uuid);
      } catch (e) {
        console.error("Failed to refresh application form", e);
      }
    } finally {
      window.progressBar.complete();
      applicationForm.submitting = false;
    }

    return false;
  };

  const reset = () => {
    screeningQuestionAnswers.value = [];
    newUserDetails.value = {...defaultNewUserDetails};
    clearProfileDraft();
    showCandidateCompleteProfile.value = false;
    usedResumeParser.value = false;
    emailCheckedAt.value = null;
    phoneCheckedAt.value = null;
    lastPhoneCodeSentTo.value = false;
    verifiedPhoneNumber.value = null;
  };

  const $reset = () => reset();

  return {
    applicationForm,
    screeningQuestionAnswers,
    newUserDetails,
    newUserPassword,
    state,
    furthestStep,
    showCandidateCompleteProfile,
    usedResumeParser,
    emailCheckedAt,
    phoneCheckedAt,
    lastPhoneCodeSentTo,
    verifiedPhoneNumber,
    handleCreateNewUser,
    setFurthestStep,
    setShowCandidateCompleteProfile,
    getApplicationForm,
    handleSubmit,
    reset,
    $reset
  };
});
